"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlayerPosition = exports.unitForPosition = exports.PositionUnit = void 0;
const depth_chart_position_1 = require("./depth_chart_position");
/**
 * The unit that a specific player plays on in an NFL game.
 */
var PositionUnit;
(function (PositionUnit) {
    /**
     * The offensive unit.
     */
    PositionUnit["Offense"] = "offense";
    /**
     * The defensive unit.
     */
    PositionUnit["Defense"] = "defense";
    /**
     * The special teams unit.
     */
    PositionUnit["SpecialTeams"] = "special-teams";
})(PositionUnit || (exports.PositionUnit = PositionUnit = {}));
/**
 * Returns the unit (offense, defense, or special teams) for a given player position or depth chart position.
 * @param position - The player position or depth chart position to get the unit for.
 * @returns The unit (offense, defense, or special teams) for the given position, or undefined if the position is not recognized.
 **/
function unitForPosition(position) {
    switch (position) {
        case PlayerPosition.Center:
        case PlayerPosition.Guard:
        case PlayerPosition.Tackle:
        case PlayerPosition.Quarterback:
        case PlayerPosition.HalfBack:
        case PlayerPosition.FullBack:
        case PlayerPosition.WideReceiver:
        case PlayerPosition.TightEnd:
            return PositionUnit.Offense;
        case PlayerPosition.DefensiveInterior:
        case PlayerPosition.Linebacker:
        case PlayerPosition.Safety:
        case PlayerPosition.Cornerback:
        case PlayerPosition.EdgeDefender:
            return PositionUnit.Defense;
        case PlayerPosition.Kicker:
        case PlayerPosition.KickReturner:
        case PlayerPosition.Punter:
        case PlayerPosition.LongSnapper:
            return PositionUnit.SpecialTeams;
        case depth_chart_position_1.DepthChartPosition.Position.DefensiveRightTackle:
        case depth_chart_position_1.DepthChartPosition.Position.DefensiveLeftTackle:
        case depth_chart_position_1.DepthChartPosition.Position.DefensiveLeftEnd:
        case depth_chart_position_1.DepthChartPosition.Position.DefensiveRightEnd:
            return PositionUnit.Defense;
        case depth_chart_position_1.DepthChartPosition.Position.WeakSideLinebacker:
        case depth_chart_position_1.DepthChartPosition.Position.StrongSideLineBacker:
        case depth_chart_position_1.DepthChartPosition.Position.MiddleLinebacker:
        case depth_chart_position_1.DepthChartPosition.Position.LeftInsideLinebacker:
        case depth_chart_position_1.DepthChartPosition.Position.LeftOutsideLinebacker:
        case depth_chart_position_1.DepthChartPosition.Position.RightInsideLinebacker:
        case depth_chart_position_1.DepthChartPosition.Position.RightOutsideLinebacker:
            return PositionUnit.Defense;
        case depth_chart_position_1.DepthChartPosition.Position.LeftTackle:
        case depth_chart_position_1.DepthChartPosition.Position.RightTackle:
        case depth_chart_position_1.DepthChartPosition.Position.Quarterback:
        case depth_chart_position_1.DepthChartPosition.Position.FullBack:
        case depth_chart_position_1.DepthChartPosition.Position.HalfBack:
        case depth_chart_position_1.DepthChartPosition.Position.RightGuard:
        case depth_chart_position_1.DepthChartPosition.Position.LeftGuard:
        case depth_chart_position_1.DepthChartPosition.Position.Center:
            return PositionUnit.Offense;
        case depth_chart_position_1.DepthChartPosition.Position.NoseTackle:
        case depth_chart_position_1.DepthChartPosition.Position.FreeSafety:
        case depth_chart_position_1.DepthChartPosition.Position.StrongSafety:
            return PositionUnit.Defense;
        case depth_chart_position_1.DepthChartPosition.Position.LeftCornerback:
        case depth_chart_position_1.DepthChartPosition.Position.RightCornerback:
        case depth_chart_position_1.DepthChartPosition.Position.SlotCornerback:
        case depth_chart_position_1.DepthChartPosition.Position.Nickelback:
            return PositionUnit.Defense;
        case depth_chart_position_1.DepthChartPosition.Position.Kicker:
        case depth_chart_position_1.DepthChartPosition.Position.KickerReturner:
        case depth_chart_position_1.DepthChartPosition.Position.Punter:
        case depth_chart_position_1.DepthChartPosition.Position.PuntReturner:
        case depth_chart_position_1.DepthChartPosition.Position.LongSnapper:
            return PositionUnit.SpecialTeams;
        case depth_chart_position_1.DepthChartPosition.Position.LeftWideReceiver:
        case depth_chart_position_1.DepthChartPosition.Position.SlotWideReceiver:
        case depth_chart_position_1.DepthChartPosition.Position.RightWideReceiver:
        case depth_chart_position_1.DepthChartPosition.Position.SlotLeftWideReceiver:
        case depth_chart_position_1.DepthChartPosition.Position.SlotRightWideReceiver:
            return PositionUnit.Offense;
        case depth_chart_position_1.DepthChartPosition.Position.LeftTightEnd:
        case depth_chart_position_1.DepthChartPosition.Position.RightTightEnd:
            return PositionUnit.Offense;
        case depth_chart_position_1.DepthChartPosition.Position.LeftEnd:
        case depth_chart_position_1.DepthChartPosition.Position.RightEnd:
            return PositionUnit.Defense;
        default:
            return undefined;
    }
}
exports.unitForPosition = unitForPosition;
/**
 * Enum that defines the position on the field a player plays.
 */
var PlayerPosition;
(function (PlayerPosition) {
    /**
     * Offensive position: Center.
     */
    PlayerPosition["Center"] = "C";
    /**
     * Defensive position: Cornerback.
     */
    PlayerPosition["Cornerback"] = "CB";
    /**
     * Defensive position: Defensive Interior.
     */
    PlayerPosition["DefensiveInterior"] = "DI";
    /**
     * Defensive position: Edge Defender.
     */
    PlayerPosition["EdgeDefender"] = "ED";
    /**
     * Offensive position: Fullback.
     */
    PlayerPosition["FullBack"] = "FB";
    /**
     * Offensive position: Guard.
     */
    PlayerPosition["Guard"] = "G";
    /**
     * Offensive position: Halfback.
     */
    PlayerPosition["HalfBack"] = "HB";
    /**
     * Special Teams position: Kicker.
     */
    PlayerPosition["Kicker"] = "K";
    /**
     * Special Teams position: Kick Returner.
     */
    PlayerPosition["KickReturner"] = "KR";
    /**
     * Defensive position: Linebacker.
     */
    PlayerPosition["Linebacker"] = "LB";
    /** Defensive Position: Defensive Back */
    PlayerPosition["DefensiveBack"] = "DB";
    /** Defensive Position: Defensive Line */
    PlayerPosition["DefensiveLine"] = "DL";
    /** Offensive Position: Offensive Line */
    PlayerPosition["OffensiveLine"] = "OL";
    /**
     * Special Teams position: Long Snapper.
     */
    PlayerPosition["LongSnapper"] = "LS";
    /**
     * Special Teams position: Punter.
     */
    PlayerPosition["Punter"] = "P";
    /**
     * Offensive position: Quarterback.
     */
    PlayerPosition["Quarterback"] = "QB";
    /**
     * Defensive position: Safety.
     */
    PlayerPosition["Safety"] = "S";
    /**
     * Offensive position: Tackle.
     */
    PlayerPosition["Tackle"] = "T";
    /**
     * Offensive position: Tight End.
     */
    PlayerPosition["TightEnd"] = "TE";
    /**
     * Offensive position: Wide Receiver.
     */
    PlayerPosition["WideReceiver"] = "WR";
})(PlayerPosition || (exports.PlayerPosition = PlayerPosition = {}));
//# sourceMappingURL=player_position.js.map